import React, { useState, useEffect, useRef } from 'react';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoClose, IoTime, IoCheckmark, IoCloseCircle, IoHome, IoPeople, IoPersonAdd } from 'react-icons/io5';
import { FaUserPlus, FaUserCheck, FaUserTimes, FaInbox, FaPaperPlane, FaCheck } from 'react-icons/fa';
import { useSelector } from 'react-redux';
import ProfileCard from '../../../components/ProfileCard';

const PendingRequestsDialog = ({ 
  isOpen, 
  onClose, 
  onAccept, 
  onReject,
  processingRequestId
}) => {
  const [activeTab, setActiveTab] = useState('incoming');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showProfileCard, setShowProfileCard] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const allPendingRequests = useSelector((state) => state.connections.pendingConnections);
  
  // Use ref to store the current state of selectedUser for event handlers
  const selectedUserRef = useRef(null);

  // Update ref when state changes
  useEffect(() => {
    selectedUserRef.current = selectedUser;
  }, [selectedUser]);

  // Debug log the pending requests when they change
  useEffect(() => {
    if (isOpen) {
      console.log('Incoming Requests:', allPendingRequests.incoming);
      console.log('Outgoing Requests:', allPendingRequests.outgoing);
    }
  }, [isOpen, allPendingRequests]);

  // Add this useEffect to log when the dialog opens
  useEffect(() => {
    if (isOpen) {
      console.log('PendingRequestsDialog opened');
      console.log('Current allPendingRequests state:', {
        incoming: allPendingRequests?.incoming?.length || 0,
        outgoing: allPendingRequests?.outgoing?.length || 0,
        incomingDetails: allPendingRequests?.incoming,
        outgoingDetails: allPendingRequests?.outgoing
      });
    }
  }, [isOpen, allPendingRequests]);

  // Reset selected user when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedUser(null);
      setShowProfileCard(false);
    }
  }, [isOpen]);

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING':
        return <IoTime className="text-pri-yellow" />;
      case 'ACCEPTED':
        return <IoCheckmark className="text-green-500" />;
      case 'REJECTED':
        return <IoClose className="text-red-500" />;
      default:
        return <IoTime className="text-pri-gray" />;
    }
  };

  // Helper function to get user profile image URL with fallback
  const getProfileImageUrl = (user) => {
    if (!user) return null;
    
    // Try all possible image properties
    const imageUrl = user.profilePhotoUrl || 
           user.profile_photo_url || 
           user.avatar || 
           user.imageUrl || 
           user.image ||
           user.photo || 
           null;
    
    console.log('Profile image URL found:', imageUrl, 'for user:', user);
    return imageUrl;
  };

  // Helper function to get user name with fallback
  const getUserName = (user, fallbackName = 'Unknown User') => {
    if (!user) return fallbackName;
    
    // Try all possible name properties
    const name = user.name || 
           (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : null) ||
           user.firstName || 
           user.username || 
           fallbackName;
    
    console.log('User name found:', name, 'for user:', user);
    return name;
  };

  // Helper function to get requester data from incoming request
  const getRequesterData = (request) => {
    console.log('Processing requester data for request:', request);
    
    // First try to get data directly from the request
    const name = request.requesterName || 
                request.requester_name || 
                (request.name && request.id !== request.recipientId ? request.name : null) ||
                (request.requester ? getUserName(request.requester) : null) ||
                'Unknown User';
    
    // Try to get profile photo from various possible locations
    let profilePhotoUrl = null;
    
    // Check direct properties first - add requesterAvatar as the first priority
    if (request.requesterAvatar) {
      profilePhotoUrl = request.requesterAvatar;
      console.log('Found requesterAvatar:', profilePhotoUrl);
    } else if (request.requesterProfilePhotoUrl) {
      profilePhotoUrl = request.requesterProfilePhotoUrl;
    } else if (request.requester_profile_photo_url) {
      profilePhotoUrl = request.requester_profile_photo_url;
    } else if (request.requesterPhoto) {
      profilePhotoUrl = request.requesterPhoto;
    } else if (request.requester_photo) {
      profilePhotoUrl = request.requester_photo;
    } else if (request.profilePhotoUrl && request.id !== request.recipientId) {
      // If this is the requester's profile photo
      profilePhotoUrl = request.profilePhotoUrl;
    } else if (request.requester) {
      // Try to get from requester object
      profilePhotoUrl = getProfileImageUrl(request.requester);
    }
    
    const initial = name.charAt(0).toUpperCase();
    
    const result = { name, profilePhotoUrl, initial };
    console.log('Requester data result:', result);
    return result;
  };

  // Helper function to get recipient data from outgoing request
  const getRecipientData = (request) => {
    console.log('Processing recipient data for request:', request);
    
    // First try to get data directly from the request
    const name = request.recipientName || 
                request.recipient_name || 
                (request.name && request.id === request.recipientId ? request.name : null) ||
                (request.recipient ? getUserName(request.recipient) : null) ||
                'Unknown User';
    
    // Try to get profile photo from various possible locations
    let profilePhotoUrl = null;
    
    // Check direct properties first - add recipientAvatar as the first priority
    if (request.recipientAvatar) {
      profilePhotoUrl = request.recipientAvatar;
      console.log('Found recipientAvatar:', profilePhotoUrl);
    } else if (request.recipientProfilePhotoUrl) {
      profilePhotoUrl = request.recipientProfilePhotoUrl;
    } else if (request.recipient_profile_photo_url) {
      profilePhotoUrl = request.recipient_profile_photo_url;
    } else if (request.recipientPhoto) {
      profilePhotoUrl = request.recipientPhoto;
    } else if (request.recipient_photo) {
      profilePhotoUrl = request.recipient_photo;
    } else if (request.profilePhotoUrl && request.id === request.recipientId) {
      // If this is the recipient's profile photo
      profilePhotoUrl = request.profilePhotoUrl;
    } else if (request.recipient) {
      // Try to get from recipient object
      profilePhotoUrl = getProfileImageUrl(request.recipient);
    }
    
    const initial = name.charAt(0).toUpperCase();
    
    const result = { name, profilePhotoUrl, initial };
    console.log('Recipient data result:', result);
    return result;
  };

  // Function to handle user profile click
  const handleUserProfileClick = (request, isIncoming = true) => {
    console.log('======== CLICKED VIEW PROFILE ========');
    console.log('Request object received:', request);
    console.log('Is incoming request:', isIncoming);
    
    // Get the user data
    const userData = isIncoming ? request.requester : request.recipient;
    console.log('Raw userData from request:', userData);
    
    // Extra safety check - make sure we have a valid ID
    const userId = isIncoming ? 
      (request.requesterId || request.requester_id || (userData?.id)) : 
      (request.recipientId || request.recipient_id || (userData?.id));
      
    if (!userId) {
      console.error('Unable to find valid user ID in the request object:', request);
    }
    
    // Create a user object to display in the profile card
    const userToShow = {
      id: userId,
      name: isIncoming ? getRequesterData(request).name : getRecipientData(request).name,
      profilePhotoUrl: isIncoming ? getRequesterData(request).profilePhotoUrl : getRecipientData(request).profilePhotoUrl,
      jobTitle: userData?.jobTitle || userData?.job_title || userData?.occupation || userData?.title || "User",
      username: userData?.username,
      location: userData?.location,
      bio: userData?.bio || userData?.about,
      email: userData?.email,
      phone: userData?.phone || userData?.phoneNumber,
      website: userData?.website || userData?.webUrl
    };
    
    console.log('Final user object for profile card:', userToShow);
    setSelectedUser(userToShow);
    setShowProfileCard(true);
  };

  // Function to handle follow user
  const handleFollowUser = (userId) => {
    console.log('Following user with ID:', userId);
    // Implement your follow logic here
    alert(`Following user ${userId}`); // Simple feedback for testing
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    }
  };

  // Generate avatar URL for users without profile photos
  const generateAvatarUrl = (name) => {
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random&color=fff&size=128`;
  };

  // Helper function to get user details
  const getUserDetails = (request, isIncoming = true) => {
    console.log(`Getting user details for ${isIncoming ? 'incoming' : 'outgoing'} request:`, request);
    
    const userData = isIncoming ? request.requester : request.recipient;
    
    if (!userData) {
      console.warn(`No user data found in ${isIncoming ? 'requester' : 'recipient'} field of request:`, request);
      return {
        name: `User (${(isIncoming ? request.requesterId : request.recipientId)?.substring(0, 8) || '?'})`,
        initial: '?',
        type: request.requestType || 'Connection'
      };
    }

    // Get the name from various possible properties
    const name = userData.name || 
                 (userData.firstName && userData.lastName ? `${userData.firstName} ${userData.lastName}` : null) ||
                 userData.username || 
                 `User (${userData.id?.substring(0, 8) || '?'})`;

    console.log(`Found user name: "${name}" for ${isIncoming ? 'requester' : 'recipient'}:`, userData);

    // Get the connection type
    const type = request.requestType || 'Connection';
    
    // Get the profile photo URL
    const profilePhotoUrl = userData.profilePhotoUrl || 
                          userData.profile_photo_url || 
                          userData.avatar || 
                          userData.imageUrl || 
                          userData.image ||
                          userData.photo;

    // Get the initial for the avatar
    const initial = name.charAt(0).toUpperCase();

    return {
      name,
      initial,
      type,
      profilePhotoUrl
    };
  };

  // Make sure we have improved accept/reject button handlers
  const handleAccept = async (request) => {
    try {
      await onAccept(request);
    } catch (error) {
      // Handle error silently
    }
  };

  const handleReject = async (request) => {
    try {
      await onReject(request);
    } catch (error) {
      // Handle error silently
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <Transition show={isOpen} as={React.Fragment}>
            <HeadlessDialog
              as="div"
              className="fixed inset-0 z-50 overflow-y-auto"
              onClose={onClose}
            >
              <div className="min-h-screen px-4 flex items-center justify-center">
                <Transition.Child
                  as={motion.div}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-pri-dark/70 backdrop-blur-sm"
                  onClick={onClose}
                />

                <Transition.Child
                  as={motion.div}
                  className="relative max-w-xl w-full mx-auto rounded-2xl shadow-2xl overflow-hidden"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                  transition={{ type: "spring", damping: 25 }}
                >
                  {/* Glossy Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-sec-dark to-pri-dark">
                    <div className="absolute inset-0 overflow-hidden">
                      <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-mint-green/5" />
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent" />
                      <div className="absolute -left-24 -top-24 w-48 h-48 bg-soft-pink/20 rounded-full filter blur-[80px] opacity-30" />
                      <div className="absolute -right-24 -bottom-24 w-48 h-48 bg-mint-green/20 rounded-full filter blur-[80px] opacity-20" />
                      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/30 to-transparent" />
                      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="relative p-6">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-6">
                      <HeadlessDialog.Title className="text-2xl font-bold bg-gradient-to-r from-soft-pink to-mint-green bg-clip-text text-transparent">
                        Connection Requests
                      </HeadlessDialog.Title>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={onClose}
                        className="p-2 rounded-full bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 border border-soft-pink/20 hover:border-soft-pink/40 transition-all"
                      >
                        <IoClose className="h-5 w-5 text-pri-white" />
                      </motion.button>
                    </div>

                    {/* Tabs */}
                    <div className="flex space-x-2 mb-6">
                      <motion.button
                        whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setActiveTab('incoming')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                          activeTab === 'incoming'
                            ? 'bg-gradient-to-br from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg'
                            : 'bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 text-pri-white border border-soft-pink/20'
                        }`}
                      >
                        <FaInbox className={activeTab === 'incoming' ? 'text-pri-dark' : 'text-soft-pink'} />
                        <span>Incoming</span>
                        {allPendingRequests?.incoming?.length > 0 && (
                          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-pri-yellow text-pri-dark text-xs font-bold">
                            {allPendingRequests.incoming.length}
                          </span>
                        )}
                      </motion.button>
                      
                      <motion.button
                        whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => setActiveTab('outgoing')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${
                          activeTab === 'outgoing'
                            ? 'bg-gradient-to-br from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg'
                            : 'bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 text-pri-white border border-soft-pink/20'
                        }`}
                      >
                        <FaPaperPlane className={activeTab === 'outgoing' ? 'text-pri-dark' : 'text-mint-green'} />
                        <span>Outgoing</span>
                        {allPendingRequests?.outgoing?.length > 0 && (
                          <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-pri-yellow text-pri-dark text-xs font-bold">
                            {allPendingRequests.outgoing.length}
                          </span>
                        )}
                      </motion.button>
                    </div>

                    {/* Request Lists */}
                    <div className="overflow-y-auto max-h-[400px] pr-1 scrollbar-thin scrollbar-thumb-soft-pink/20 scrollbar-track-transparent">
                      <AnimatePresence mode="wait">
                        {activeTab === 'incoming' ? (
                          <motion.div
                            key="incoming"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 10 }}
                            transition={{ duration: 0.2 }}
                          >
                            {allPendingRequests?.incoming?.length > 0 ? (
                              <div className="space-y-3">
                                {allPendingRequests.incoming.map((request) => {
                                  const requesterData = getRequesterData(request);
                                  const isProcessing = processingRequestId === (request.id || request._id || request.requestId);
                                  
                                  return (
                                    <motion.div
                                      key={request.id || request._id || request.requestId || `incoming-${Date.now()}`}
                                      whileHover={{ scale: 1.02 }}
                                      className="p-4 rounded-xl bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 border border-soft-pink/10 shadow-lg"
                                    >
                                      <div className="flex items-center space-x-3">
                                        {/* Profile Image - Clickable */}
                                        <motion.div 
                                          className="w-12 h-12 flex-shrink-0 cursor-pointer"
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                          onClick={() => handleUserProfileClick(request, true)}
                                        >
                                          {requesterData.profilePhotoUrl ? (
                                            <img
                                              src={requesterData.profilePhotoUrl}
                                              alt={requesterData.name}
                                              className="w-full h-full rounded-full object-cover border border-soft-pink/20"
                                            />
                                          ) : (
                                            <div className="w-full h-full rounded-full flex items-center justify-center bg-gradient-to-br from-soft-pink/20 to-mint-green/20 text-white">
                                              {requesterData.initial}
                                            </div>
                                          )}
                                        </motion.div>
                                        
                                        {/* Request Info */}
                                        <div className="flex-1 min-w-0">
                                          {/* Name - Clickable */}
                                          <motion.h4 
                                            className="font-semibold text-pri-white truncate cursor-pointer hover:text-soft-pink transition-colors flex items-center group"
                                            onClick={() => handleUserProfileClick(request, true)}
                                          >
                                            {requesterData.name}
                                            <span className="ml-1 text-xs text-soft-pink/70 group-hover:text-soft-pink transition-colors">
                                              (view profile)
                                            </span>
                                          </motion.h4>
                                          <p className="text-sm text-pri-gray">
                                            Wants to connect as
                                            <span className="text-soft-pink ml-1">
                                              {request.requestType?.charAt(0).toUpperCase() + request.requestType?.slice(1) || 'Friend'}
                                            </span>
                                          </p>
                                        </div>
                                      </div>
                                      
                                      {/* Action Buttons */}
                                      <div className="flex justify-end mt-3 space-x-2">
                                        <motion.button
                                          whileHover={{ scale: 1.05, boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)' }}
                                          whileTap={{ scale: 0.95 }}
                                          disabled={isProcessing}
                                          onClick={() => onReject(request.id || request._id || request.requestId)}
                                          className="p-2 rounded-lg bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 border border-soft-pink/20 text-soft-pink"
                                        >
                                          <IoCloseCircle className="h-5 w-5" />
                                        </motion.button>
                                        <motion.button
                                          whileHover={{ scale: 1.05, boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)' }}
                                          whileTap={{ scale: 0.95 }}
                                          disabled={isProcessing}
                                          onClick={() => onAccept(request.id || request._id || request.requestId, request.requestType)}
                                          className="px-4 py-2 rounded-lg bg-gradient-to-br from-soft-pink to-mint-green text-pri-dark font-medium shadow-lg flex items-center gap-2"
                                        >
                                          <FaUserCheck className="h-4 w-4" />
                                          <span>Accept</span>
                                        </motion.button>
                                      </div>
                                    </motion.div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center py-10 rounded-xl bg-gradient-to-br from-pri-dark/40 to-sec-dark/40 border border-soft-pink/10">
                                <div className="p-4 rounded-full bg-gradient-to-br from-soft-pink/10 to-mint-green/10 mb-4">
                                  <FaInbox className="h-8 w-8 text-pri-gray" />
                                </div>
                                <p className="text-pri-gray">No incoming requests</p>
                              </div>
                            )}
                          </motion.div>
                        ) : (
                          <motion.div
                            key="outgoing"
                            initial={{ opacity: 0, x: 10 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -10 }}
                            transition={{ duration: 0.2 }}
                          >
                            {allPendingRequests?.outgoing?.length > 0 ? (
                              <div className="space-y-3">
                                {allPendingRequests.outgoing.map((request) => {
                                  const recipientData = getRecipientData(request);
                                  
                                  return (
                                    <motion.div
                                      key={request.id || request._id || request.requestId || `outgoing-${Date.now()}`}
                                      whileHover={{ scale: 1.02 }}
                                      className="p-4 rounded-xl bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 border border-soft-pink/10 shadow-lg"
                                    >
                                      <div className="flex items-center space-x-3">
                                        {/* Profile Image - Clickable */}
                                        <motion.div 
                                          className="w-12 h-12 flex-shrink-0 cursor-pointer"
                                          whileHover={{ scale: 1.05 }}
                                          whileTap={{ scale: 0.95 }}
                                          onClick={() => handleUserProfileClick(request, false)}
                                        >
                                          {recipientData.profilePhotoUrl ? (
                                            <img
                                              src={recipientData.profilePhotoUrl}
                                              alt={recipientData.name}
                                              className="w-full h-full rounded-full object-cover border border-soft-pink/20"
                                            />
                                          ) : (
                                            <div className="w-full h-full rounded-full flex items-center justify-center bg-gradient-to-br from-soft-pink/20 to-mint-green/20 text-white">
                                              {recipientData.initial}
                                            </div>
                                          )}
                                        </motion.div>
                                        
                                        {/* Request Info */}
                                        <div className="flex-1 min-w-0">
                                          {/* Name - Clickable */}
                                          <motion.h4 
                                            className="font-semibold text-pri-white truncate cursor-pointer hover:text-soft-pink transition-colors flex items-center group"
                                            onClick={() => handleUserProfileClick(request, false)}
                                          >
                                            {recipientData.name}
                                            <span className="ml-1 text-xs text-soft-pink/70 group-hover:text-soft-pink transition-colors">
                                              (view profile)
                                            </span>
                                          </motion.h4>
                                          <p className="flex items-center text-sm text-pri-gray">
                                            <IoTime className="h-3 w-3 mr-1 text-pri-yellow" />
                                            Waiting for response
                                          </p>
                                        </div>
                                      </div>
                                      
                                      <div className="mt-2">
                                        <p className="text-sm text-pri-gray">
                                          <span className="text-soft-pink">
                                            {request.requestType?.charAt(0).toUpperCase() + request.requestType?.slice(1) || 'Friend'}
                                          </span> request sent on {formatDate(request.createdAt)}
                                        </p>
                                      </div>
                                    </motion.div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center justify-center py-10 rounded-xl bg-gradient-to-br from-pri-dark/40 to-sec-dark/40 border border-soft-pink/10">
                                <div className="p-4 rounded-full bg-gradient-to-br from-soft-pink/10 to-mint-green/10 mb-4">
                                  <FaPaperPlane className="h-8 w-8 text-pri-gray" />
                                </div>
                                <p className="text-pri-gray">No outgoing requests</p>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </Transition.Child>
              </div>
            </HeadlessDialog>
          </Transition>
        )}
      </AnimatePresence>
      
      {/* Profile Card Modal - Simple Direct Implementation */}
      {showProfileCard && selectedUser && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-lg"
             style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0}}
        >
          <div 
            className="absolute inset-0"
            onClick={() => {
              console.log('Backdrop clicked, closing profile card');
              setShowProfileCard(false);
            }}
          ></div>
          
          <div 
            className="relative max-w-[90vw] z-[10000] bg-gradient-to-br from-[#1a1f36]/80 to-[#121524]/80 p-1 rounded-xl backdrop-blur-md shadow-2xl border border-soft-pink/30"
            onClick={(e) => e.stopPropagation()}
          >
            <ProfileCard 
              user={selectedUser}
              onClose={() => {
                console.log('Close button clicked on profile card');
                setShowProfileCard(false);
              }}
              style={{ 
                maxWidth: '280px',
                width: '100%'
              }}
            />
          </div>
        </div>
      )}
    </>
  );
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return 'Unknown date';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default PendingRequestsDialog; 